/**
 * Shared utility functions for WeaveProjectTab components
 */

import {PEEK_PARAM} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/context';
import {CallSchema} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/wfDataModelHooksInterface';

import {
  COST_MIN_THRESHOLD,
  LATENCY_MS_THRESHOLD,
  LATENCY_S_THRESHOLD,
  MS_PER_DAY,
  MS_PER_HOUR,
  TOKEN_MILLION_THRESHOLD,
  TOKEN_THOUSAND_THRESHOLD,
} from './constants';

/**
 * Format a timestamp as a relative time string (e.g., "2h ago", "3d ago")
 */
export function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const hours = Math.floor(diff / MS_PER_HOUR);
  const days = Math.floor(diff / MS_PER_DAY);

  if (hours < 1) {
    return 'Just now';
  }
  if (hours < 24) {
    return `${hours}h ago`;
  }
  return `${days}d ago`;
}

/**
 * Format latency in seconds to a human-readable string (e.g., "150ms", "2.34s")
 * Returns null if latency is undefined/null
 */
export function formatLatency(latency?: number): string | null {
  if (!latency) {
    return null;
  }
  if (latency < LATENCY_MS_THRESHOLD) {
    return '<10ms';
  }
  if (latency < LATENCY_S_THRESHOLD) {
    return `${(latency * 1000).toFixed(0)}ms`;
  }
  return `${latency.toFixed(2)}s`;
}

/**
 * Format token count with K/M suffixes (e.g., "1.5K", "2.3M")
 * Returns null if tokens is undefined/null/0
 */
export function formatTokens(tokens?: number): string | null {
  if (!tokens) {
    return null;
  }
  if (tokens >= TOKEN_MILLION_THRESHOLD) {
    return `${(tokens / TOKEN_MILLION_THRESHOLD).toFixed(1)}M`;
  }
  if (tokens >= TOKEN_THOUSAND_THRESHOLD) {
    return `${(tokens / TOKEN_THOUSAND_THRESHOLD).toFixed(1)}K`;
  }
  return tokens.toString();
}

/**
 * Convert a number to undefined if it's 0 or falsy.
 * Useful for filtering out zero values from optional metric fields.
 */
export function toOptionalMetric(
  value: number | undefined
): number | undefined {
  return value && value > 0 ? value : undefined;
}

/**
 * Format cost as USD currency (e.g., "$0.0123", "<$0.0001")
 * Returns null if cost is undefined/null
 */
export function formatCost(cost?: number): string | null {
  if (cost === undefined || cost === null) {
    return null;
  }
  if (cost !== 0 && cost < COST_MIN_THRESHOLD) {
    return '<$0.0001';
  }
  return `$${cost.toFixed(4)}`;
}

/**
 * Format a date for object cards (e.g., "Today", "3d ago", "Jan 15")
 */
export function formatObjectCardDate(createdAt?: string): string {
  if (!createdAt) {
    return '';
  }
  const date = new Date(createdAt);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / MS_PER_DAY);

  if (days === 0) {
    return 'Today';
  }
  if (days === 1) {
    return 'Yesterday';
  }
  if (days < 7) {
    return `${days}d ago`;
  }
  if (days < 30) {
    return `${Math.floor(days / 7)}w ago`;
  }
  return date.toLocaleDateString(undefined, {month: 'short', day: 'numeric'});
}

/**
 * Calculate total tokens and cost from a call's summary.
 * This handles different token field names (input_tokens/prompt_tokens, output_tokens/completion_tokens)
 * and aggregates costs across all models.
 */
export function calculateCallMetrics(call: CallSchema): {
  totalTokens: number;
  totalCost: number;
} {
  // Usage and cost fields have dynamic structure based on model names
  // Type as 'any' to safely access dynamic keys
  const usage = call.traceCall?.summary?.usage as any;
  const costs = call.traceCall?.summary?.weave?.costs as any;
  let totalTokens = 0;
  let totalCost = 0;

  // Aggregate tokens from usage summary
  if (usage) {
    Object.keys(usage).forEach(model => {
      const promptTokens =
        usage[model]?.input_tokens ?? usage[model]?.prompt_tokens ?? 0;
      const completionTokens =
        usage[model]?.output_tokens ?? usage[model]?.completion_tokens ?? 0;
      totalTokens += promptTokens + completionTokens;
    });
  }

  // Aggregate costs across models
  if (costs) {
    Object.keys(costs).forEach(model => {
      const promptCost = costs[model]?.prompt_tokens_total_cost ?? 0;
      const completionCost = costs[model]?.completion_tokens_total_cost ?? 0;
      totalCost += promptCost + completionCost;
    });
  }

  return {totalTokens, totalCost};
}

/**
 * Calculate latency in seconds from start and end timestamps.
 * Returns undefined if either timestamp is missing.
 */
export function calculateLatency(
  startedAt?: string,
  endedAt?: string
): number | undefined {
  if (!startedAt || !endedAt) {
    return undefined;
  }
  return (new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 1000;
}

/**
 * Generate a route with peek parameter for Weave UI
 * @param baseRoute - The base route path (e.g., 'traces', 'evaluations', 'models')
 * @param peekPath - The path to peek at
 * @param entityName - The entity name
 * @param projectName - The project name
 */
export function buildPeekRoute(
  baseRoute: string,
  peekPath: string,
  entityName: string,
  projectName: string
): string {
  return `/${entityName}/${projectName}/weave/${baseRoute}?${PEEK_PARAM}=${encodeURIComponent(peekPath)}`;
}

/**
 * Generate a route for a call (trace or evaluation)
 */
export function buildCallRoute(
  callId: string,
  baseRoute: 'traces' | 'evaluations',
  entityName: string,
  projectName: string
): string {
  const callPath = `/${entityName}/${projectName}/calls/${callId}`;
  return buildPeekRoute(baseRoute, callPath, entityName, projectName);
}

/**
 * Generate a route for an object version (model or dataset)
 */
export function buildObjectVersionRoute(
  objectName: string,
  digest: string,
  objectType: 'model' | 'dataset',
  entityName: string,
  projectName: string
): string {
  const objectPath = `/${entityName}/${projectName}/objects/${objectName}/versions/${digest}`;
  const baseRoute = objectType === 'model' ? 'models' : 'datasets';
  return buildPeekRoute(baseRoute, objectPath, entityName, projectName);
}
